"use client";

import { useLocale } from "next-intl";
import { use, useEffect, useEffectEvent } from "react";
import { Controller, Provider } from "@components/store-locator/controller";
import { StoreLocatorUi } from "@components/store-locator/StoreLocator";
import { activeSiteConfig } from "@config/site/site-config";
import { useAuth } from "@lib/auth/useAuth";
import { LogTag, Logger, ServiceType } from "@lib/monitoring/logger";
import { fetchStores, normalizeStore } from "../data";
import type { JSX } from "react";

const StoreFinderContent = (): JSX.Element => {
    const { session, status } = useAuth();
    const locale = useLocale();
    const { country } = activeSiteConfig;
    const { setStoreData, setIsLoading } = use(Controller);
    const hasSession = Boolean(session);

    const loadStores = useEffectEvent(() => {
        if (status !== "authenticated" || !session) {
            setIsLoading(false);
            return;
        }

        let isActive = true;
        setIsLoading(true);
        fetchStores(country, true, session.accessToken)
            .then((data) => {
                if (isActive) {
                    setStoreData(normalizeStore(data, locale));
                }
            })
            .catch((error) => {
                if (isActive) {
                    Logger.error(ServiceType.WEB, "Error fetching stores", {
                        tag: LogTag.STORES,
                        error,
                    });
                }
            })
            .finally(() => {
                if (isActive) setIsLoading(false);
            });

        return () => {
            isActive = false;
        };
    });

    useEffect(() => {
        return loadStores();
    }, [status, hasSession]);

    return <StoreLocatorUi isPickUpSelection={false} location="storeLocator" />;
};

export const StoreFinder = (): JSX.Element => (
    <Provider storeLocatorLocation="storeLocator">
        <StoreFinderContent />
    </Provider>
);
